import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, ChevronDown, LogOut, Package, Settings, Headphones, Plus, Megaphone } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { HEALTH_CATEGORIES, categoryUrl } from '@/lib/health';
import AuthModal from './AuthModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POPULAR_CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'GHS', 'KES', 'ZAR', 'CAD', 'AUD'];

const NAV_LINKS = [
  { to: '/products', label: 'Shop all' },
  ...HEALTH_CATEGORIES.map((c) => ({ to: categoryUrl(c.name), label: c.short })),
];

export const BrandLogo = ({ size = 'md' }) => (
  <span className="flex items-center gap-2">
    <motion.span
      whileHover={{ rotate: 90 }}
      transition={{ type: 'spring', stiffness: 300, damping: 12 }}
      className={`${size === 'lg' ? 'w-11 h-11' : 'w-9 h-9'} rounded-xl bg-health-emerald text-white flex items-center justify-center shadow-soft`}
    >
      <Plus className={size === 'lg' ? 'h-7 w-7' : 'h-6 w-6'} strokeWidth={3} />
    </motion.span>
    <span className="leading-none">
      <span className={`font-heading font-bold text-health-ink tracking-wide block ${size === 'lg' ? 'text-3xl' : 'text-2xl'}`}>PACTAC</span>
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-health-coral">Health</span>
    </span>
  </span>
);

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { cart } = useCart();
  const { activeCurrency: currency, setCurrency, getAvailableCurrencies } = useCurrency();
  const currencies = getAvailableCurrencies().map((c) => c.code).filter((c) => POPULAR_CURRENCIES.includes(c));
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [livechat3cxEnabled, setLivechat3cxEnabled] = useState(false);
  const userMenuRef = useRef(null);
  const currencyMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const currentPath = location.pathname + location.search;

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/checkout-settings`)
      .then((res) => {
        if (!cancelled) setLivechat3cxEnabled(Boolean(res.data?.livechat_3cx_enabled));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false);
      if (currencyMenuRef.current && !currencyMenuRef.current.contains(event.target)) setShowCurrencyMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  // Opens the 3CX Live Chat widget by clicking its bubble inside the web component shadow root.
  const openLiveChat = () => {
    const selector = document.querySelector('call-us-selector');
    const callUs = selector?.querySelector('call-us') || document.querySelector('call-us');
    if (!callUs) return;
    const bubble = callUs.shadowRoot?.querySelector('[class*="bubble"], button.close-button, button');
    (bubble || callUs).click();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 font-body ${
          scrolled ? 'bg-white/90 backdrop-blur-xl shadow-nav border-b border-health-line' : 'bg-white/70 backdrop-blur-md'
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" data-testid="navbar-logo">
              <BrandLogo />
            </Link>

            <div className="hidden lg:flex items-center gap-1 bg-health-mint/70 rounded-full p-1">
              {NAV_LINKS.map((link) => {
                const active = currentPath === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                      active ? 'text-white' : 'text-slate-600 hover:text-health-emerald'
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {active && (
                      <motion.span layoutId="nav-active-pill" className="absolute inset-0 bg-health-emerald rounded-full" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                    )}
                    <span className="relative">{link.label}</span>
                  </Link>
                );
              })}
              {livechat3cxEnabled && (
                <button
                  onClick={openLiveChat}
                  className="px-4 py-2 text-sm font-semibold text-health-coral hover:text-health-emerald transition-colors flex items-center gap-2"
                  data-testid="nav-talk-to-sales"
                >
                  <Headphones className="h-4 w-4" />
                  Ask us
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative hidden sm:block" ref={currencyMenuRef}>
                <button
                  onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-600 hover:text-health-emerald border border-health-line bg-white rounded-full transition-colors"
                  data-testid="nav-currency-button"
                >
                  <span>{currency}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
                <AnimatePresence>
                  {showCurrencyMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      className="absolute top-full right-0 mt-2 w-28 max-h-80 overflow-y-auto bg-white border border-health-line rounded-2xl shadow-lift py-1"
                    >
                      {currencies.map((curr) => (
                        <button
                          key={curr}
                          onClick={() => { setCurrency(curr); setShowCurrencyMenu(false); }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-health-mint transition-colors ${
                            currency === curr ? 'text-health-emerald font-bold' : 'text-slate-600'
                          }`}
                          data-testid={`currency-option-${curr}`}
                        >
                          {curr}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/cart"
                className="relative w-11 h-11 flex items-center justify-center text-health-ink bg-white border border-health-line hover:border-health-emerald hover:text-health-emerald rounded-full transition-colors"
                data-testid="nav-cart-button"
              >
                <ShoppingBag className="h-5 w-5" />
                <AnimatePresence>
                  {cartCount > 0 && (
                    <motion.span
                      key={cartCount}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                      className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center bg-health-coral text-white text-xs font-bold rounded-full shadow-badge"
                      data-testid="cart-count-badge"
                    >
                      {cartCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>

              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 pl-1 pr-3 py-1 bg-white border border-health-line hover:border-health-emerald rounded-full transition-colors"
                    data-testid="nav-user-menu-button"
                  >
                    <span className="w-9 h-9 rounded-full bg-health-coral text-white flex items-center justify-center font-heading font-bold">
                      {user.full_name?.charAt(0) || 'U'}
                    </span>
                    <ChevronDown className="h-3 w-3 text-slate-500" />
                  </button>
                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        className="absolute top-full right-0 mt-2 w-56 bg-white border border-health-line rounded-2xl shadow-lift overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-health-line bg-health-mint/50">
                          <p className="text-sm font-bold text-health-ink truncate">{user.full_name}</p>
                          <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        </div>
                        <Link to="/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-health-mint transition-colors" data-testid="nav-my-orders-link">
                          <Package className="h-4 w-4 text-health-emerald" />
                          My Orders
                        </Link>
                        {user.is_affiliate && (
                          <Link to="/affiliate" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-health-mint transition-colors" data-testid="nav-affiliate-link">
                            <Megaphone className="h-4 w-4 text-health-coral" />
                            Ambassador Dashboard
                          </Link>
                        )}
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-health-mint transition-colors" data-testid="nav-admin-link">
                            <Settings className="h-4 w-4 text-health-emerald" />
                            Admin Panel
                          </Link>
                        )}
                        <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="nav-logout-button">
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="hidden md:flex bg-health-emerald hover:bg-health-emerald-dark text-white font-bold px-6 h-11 rounded-full shadow-soft"
                  data-testid="nav-login-button"
                >
                  Sign in
                </Button>
              )}

              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden w-11 h-11 flex items-center justify-center text-health-ink bg-white border border-health-line rounded-full"
                data-testid="nav-mobile-menu-button"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-health-line overflow-hidden"
              data-testid="nav-mobile-menu"
            >
              <div className="px-4 py-5 space-y-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div key={link.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                    <Link
                      to={link.to}
                      onClick={() => setShowMobileMenu(false)}
                      className="block px-4 py-3 rounded-2xl text-base font-semibold text-health-ink hover:bg-health-mint hover:text-health-emerald transition-colors"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                {livechat3cxEnabled && (
                  <button
                    onClick={() => { openLiveChat(); setShowMobileMenu(false); }}
                    className="flex items-center gap-2 px-4 py-3 text-base font-semibold text-health-coral"
                    data-testid="mobile-talk-to-sales"
                  >
                    <Headphones className="h-5 w-5" />
                    Ask us
                  </button>
                )}
                <div className="flex flex-wrap gap-2 px-4 pt-3" data-testid="mobile-currency-options">
                  {currencies.map((curr) => (
                    <button key={curr} onClick={() => setCurrency(curr)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${currency === curr ? 'bg-health-emerald text-white border-health-emerald' : 'bg-white text-slate-600 border-health-line'}`} data-testid={`mobile-currency-option-${curr}`}>
                      {curr}
                    </button>
                  ))}
                </div>
                {!user && (
                  <Button
                    onClick={() => { setShowAuthModal(true); setShowMobileMenu(false); }}
                    className="w-full bg-health-emerald hover:bg-health-emerald-dark text-white font-bold mt-3 h-12 rounded-full"
                    data-testid="mobile-login-button"
                  >
                    Sign in
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};

export default Navbar;
